/**
 * MCP 클라이언트에 붙여 넣는 명령과 연결 코드 표시 규칙.
 *
 * 명령은 다른 머신의 셸에서 실행된다. 그래서 주소는 모두 절대 URL 이다. 설치 스크립트는 MCP
 * 게이트웨이가 준다(`/mcp-install/<client>.sh`). 서버 이름 `spnp` 는 스크립트가 쓰는 이름과 같다.
 */

export type McpClient = "codex" | "claude";

const SERVER_NAME = "spnp";
const CODE_GROUP = 5;
// 따옴표 없이 sh · bash · zsh 의 인자로 써도 뜻이 바뀌지 않는 문자만 가진 http(s) 주소.
const SHELL_SAFE_URL = /^https?:\/\/[a-z0-9._:/%-]+$/i;

/** `codex mcp add` 가 로그인을 시작하지 않았을 때 실행하는 명령. */
export const CODEX_LOGIN_COMMAND = `codex mcp login ${SERVER_NAME}`;

/**
 * Claude Code 는 같은 이름의 등록을 덮어쓰지 않는다. 다시 등록하기 전에 실행하는 명령.
 *
 * 범위를 적지 않는다. Claude Code 가 등록이 있는 범위에서 지운다. 등록이 두 범위에 있으면 Claude Code 가
 * 범위를 고르는 명령을 알려 준다.
 */
export const CLAUDE_REMOVE_COMMAND = `claude mcp remove ${SERVER_NAME}`;

/**
 * 설정한 MCP 주소(`appConfig.mcpUrl`)를 쓴다. 이 값은 `VITE_MCP_URL` 이고, 없으면 `VITE_API_BASE_URL`
 * 아래의 `/mcp` 다. 값이 비어 있으면 이 화면의 origin 아래 `/mcp` 로 본다(배포는 같은 origin 이다).
 *
 * 주소는 다른 머신의 셸 명령에 따옴표 없이 들어간다. 그래서 영숫자와 `. _ : / % -` 만 가진 절대
 * http(s) URL 만 받고, 받은 주소는 바꾸지 않는다. 인증 정보 · query · fragment · IPv6 리터럴이 있는
 * 주소와 URL 로 읽을 수 없는 주소는 null 이다.
 */
export function resolveMcpUrl(configured: string, origin: string): string | null {
  try {
    const address = configured !== "" ? configured : new URL("/mcp", origin).href;
    // 문자만 맞고 URL 이 아닌 값(`http://:99999`)은 `new URL` 이 예외로 거른다.
    return SHELL_SAFE_URL.test(address) && new URL(address).host !== "" ? address : null;
  } catch {
    return null;
  }
}

/**
 * `K7QM3XPD2F` → `K7QM3-XPD2F`.
 *
 * 설치 스크립트는 하이픈을 가진 코드를 그대로 Overlord 에 보낸다. Overlord 가 하이픈을 떼고 읽는다.
 */
export function formatConnectCode(code: string): string {
  return code.length > CODE_GROUP ? `${code.slice(0, CODE_GROUP)}-${code.slice(CODE_GROUP)}` : code;
}

/** 원클릭 — 연결 코드를 토큰으로 바꾸고 클라이언트를 설정하는 한 줄. */
export function installCommand(client: McpClient, mcpUrl: string, code: string): string {
  const script = new URL(`/mcp-install/${client}.sh`, mcpUrl).href;
  return `curl -fsSL ${script} | sh -s -- ${formatConnectCode(code)}`;
}

/**
 * 로그인 — 클라이언트가 브라우저 OAuth 로 스스로 토큰을 받는다.
 *
 * - Codex: `codex mcp add` 는 서버가 OAuth 를 광고하면 로그인도 시작한다. 그래서 `codex mcp login` 을
 *   덧붙이지 않는다. 덧붙이면 로그인이 두 번 열린다.
 * - Claude Code: 설치 스크립트와 같은 user 범위에 등록한다. 기본 범위(local)는 명령을 실행한
 *   디렉터리에서만 보인다.
 */
export function loginCommand(client: McpClient, mcpUrl: string): string {
  if (client === "codex") {
    return `codex mcp add ${SERVER_NAME} --url ${mcpUrl}`;
  }
  return `claude mcp add --transport http --scope user ${SERVER_NAME} ${mcpUrl}`;
}

/** 만료까지 남은 시간(`9분 5초`). 이미 지났으면 null 이다. */
export function remainingLabel(expiresAtMs: number, nowMs: number): string | null {
  const seconds = Math.ceil((expiresAtMs - nowMs) / 1000);
  if (seconds <= 0) {
    return null;
  }
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes === 0 ? `${rest}초` : `${minutes}분 ${rest}초`;
}
